import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileMinus2, TriangleAlert, CalendarClock, Info } from "lucide-react";
import { Pagina, Cartao, Carregando, Falha } from "../shell/Pagina.js";
import { trpc } from "../lib/trpc.js";
import { data as fData, plural } from "../lib/formato.js";

const hoje = () => new Date().toISOString().slice(0, 10);

const VERBA: Record<string, string> = {
  saldo_salario: "Saldo de salário",
  aviso_previo_indenizado: "Aviso prévio indenizado",
  decimo_terceiro_proporcional: "13º proporcional",
  ferias_vencidas: "Férias vencidas",
  ferias_proporcionais: "Férias proporcionais",
  terco_ferias: "Um terço de férias",
  multa_fgts: "Multa do FGTS",
  saque_fgts: "Saque do FGTS",
  seguro_desemprego: "Seguro-desemprego",
  desconto_aviso_nao_cumprido: "Desconto do aviso não cumprido",
};

const ESTABILIDADE: Record<string, string> = {
  gestante: "Gestante", cipa: "CIPA", acidentado: "Acidentado",
  dirigente_sindical: "Dirigente sindical",
  pre_aposentadoria_cct: "Pré-aposentadoria por CCT",
};

/**
 * Simulador de rescisão.
 *
 * Usa o MESMO motor da rescisão real, em modo simulação e sem persistir nada.
 * Vale como ferramenta de negociação, porque o gestor mostra de onde vem cada
 * número, e como teste de aceitação do próprio motor.
 */
export function Rescisao() {
  const [f, setF] = useState({
    motivo: "02", dataAviso: hoje(), anosDeCasa: 2, avisoIndenizado: true,
  });
  const [estabilidades, setEstabilidades] = useState<string[]>([]);

  const q = useQuery({
    queryKey: ["rescisao", f, estabilidades],
    queryFn: () => trpc.operacao.simularRescisao.query({
      motivo: f.motivo, dataAviso: f.dataAviso, anosDeCasa: f.anosDeCasa,
      avisoIndenizado: f.avisoIndenizado,
      estabilidades: estabilidades as ("gestante")[],
    }),
  });

  const r = q.data;

  return (
    <Pagina
      secao="Folha" titulo="Rescisão"
      descricao="Simulação com o mesmo motor do cálculo real, sem gravar nada. O aviso indenizado projeta a data de saída e muda os avos de 13º e de férias."
    >
      <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_1fr] lg:items-start">
        <Cartao titulo="Cenário" subtitulo="Mude e a conta refaz sozinha">
          <div className="space-y-3.5">
            <label className="block">
              <span className="eop-rotulo">Motivo</span>
              <select className="campo mt-1.5" value={f.motivo}
                      onChange={(e) => setF((v) => ({ ...v, motivo: e.target.value }))}>
                {(r?.motivos ?? []).map((m) => (
                  <option key={m.codigo} value={m.codigo}>{m.codigo} · {m.descricao}</option>
                ))}
              </select>
            </label>
            <div className="grid gap-3.5 sm:grid-cols-2">
              <label className="block">
                <span className="eop-rotulo">Data do aviso</span>
                <input className="campo mt-1.5" type="date" value={f.dataAviso}
                       onChange={(e) => setF((v) => ({ ...v, dataAviso: e.target.value }))} />
              </label>
              <label className="block">
                <span className="eop-rotulo">Anos de casa</span>
                <input className="campo mt-1.5 num" type="number" min={0} max={60}
                       value={f.anosDeCasa}
                       onChange={(e) => setF((v) => ({ ...v, anosDeCasa: Number(e.target.value) }))} />
              </label>
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={f.avisoIndenizado}
                     onChange={(e) => setF((v) => ({ ...v, avisoIndenizado: e.target.checked }))} />
              <span className="text-sm">Aviso indenizado</span>
            </label>

            <div>
              <span className="eop-rotulo">Estabilidades</span>
              <div className="mt-2 flex flex-col gap-1.5">
                {Object.entries(ESTABILIDADE).map(([k, nome]) => (
                  <label key={k} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={estabilidades.includes(k)}
                           onChange={(e) => setEstabilidades((v) =>
                             e.target.checked ? [...v, k] : v.filter((x) => x !== k))} />
                    {nome}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </Cartao>

        <div className="space-y-4">
          {q.isPending && <Carregando linhas={5} />}
          {q.isError && <Falha erro={q.error} tentarDeNovo={() => void q.refetch()} />}

          {r?.bloqueios.bloqueado && (
            <Cartao titulo="Dispensa bloqueada"
                    subtitulo="Estabilidade impede a dispensa por este motivo">
              <ul className="eop-op">
                {r.bloqueios.motivos.map((b) => (
                  <li key={b} data-tom="critico">
                    <span aria-hidden className="eop-op-icone"><TriangleAlert size={15} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="eop-op-titulo">{b}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Cartao>
          )}

          {r && (
            <>
              <Cartao titulo="Prazos" subtitulo="O que a lei conta a partir daqui">
                <ul className="eop-op">
                  <li>
                    <span aria-hidden className="eop-op-icone"><CalendarClock size={15} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="eop-op-titulo">
                        Aviso prévio de {plural(r.aviso.dias, "dia", "dias")}
                      </span>
                      <span className="eop-op-explica">{r.aviso.fonte}</span>
                    </span>
                  </li>
                  <li>
                    <span aria-hidden className="eop-op-icone"><CalendarClock size={15} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="eop-op-titulo">
                        Saída projetada em {fData(r.projecao.dataProjetada)}
                      </span>
                      <span className="eop-op-explica">
                        {/* É o que muda os avos: usar a data crua erra o 13º de
                            quem sai em 30/11 com 30 dias de aviso indenizado. */}
                        {r.projecao.avosGanhos > 0
                          ? `a projeção acrescenta ${plural(r.projecao.avosGanhos, "avo", "avos")} de 13º e de férias`
                          : "sem avo adicional nesta configuração"}
                      </span>
                      <span className="eop-op-resolve">{r.projecao.fonte}</span>
                    </span>
                  </li>
                  <li data-tom="atencao">
                    <span aria-hidden className="eop-op-icone"><CalendarClock size={15} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="eop-op-titulo">
                        Pagar até {fData(r.prazo.limite)}
                      </span>
                      <span className="eop-op-explica">{r.prazo.fonte}</span>
                    </span>
                  </li>
                </ul>
              </Cartao>

              {r.motivo && (
                <Cartao titulo="Verbas deste motivo"
                        subtitulo={`${r.motivo.codigo} · ${r.motivo.descricao}`}>
                  <ul className="eop-op">
                    {r.motivo.verbas.map((v) => (
                      <li key={v}>
                        <span aria-hidden className="eop-op-icone"><FileMinus2 size={15} /></span>
                        <span className="min-w-0 flex-1">
                          <span className="eop-op-titulo">{VERBA[v] ?? v}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <dl className="eop-holerite-totais">
                    <div>
                      <dt>Multa do FGTS</dt>
                      <dd className="num">
                        {/* null é "não gera multa", que é diferente de "gera
                            multa de 0%": confundir os dois faz o sistema
                            calcular zero onde não deveria calcular nada. */}
                        {r.motivo.percentualMulta === null
                          ? "não gera" : `${r.motivo.percentualMulta}%`}
                      </dd>
                    </div>
                    <div>
                      <dt>Saque do FGTS</dt>
                      <dd>{r.motivo.geraSaqueFgts ? "libera" : "não libera"}</dd>
                    </div>
                  </dl>
                  <p className="carimbo mt-4">
                    <Info aria-hidden size={11} style={{ display: "inline", marginRight: 4 }} />
                    a matriz é dado, não código: motivo novo é linha na tabela, não deploy
                  </p>
                </Cartao>
              )}
            </>
          )}
        </div>
      </div>
    </Pagina>
  );
}
